<template>
  <v-container fluid class="wrapper wd-flex wd-flex-col wd-h-auto" :class="isRedemptionStep ? 'wd-w-712px' : 'wd-w-auto'">
    <template v-if="isRedemptionStep">
      <!-- ELITE intro -->
      <div class="eliteIntroSection wd-mb-6 wd-bg-white">
        <h2>{{ $t('elite_membership_redeem_title') }}</h2>
        <p class="eliteIntroSection__description mb-0 wd-py-4">{{ $t('elite_membership_redeem_description') }}</p>
      </div>

      <!-- Credit card detail -->
      <div class="creditCardDetailSection wd-bg-white">
        <h2>{{ $t('elite_credit_card_detail_title') }}</h2>
        <div class="creditCardDetailSection__content">
          <Form ref="validationObserverRef" v-slot="{ meta }" as="form" @submit="handleVerify">
            <p class="mb-n2 wd-leading-5" style="font-size: 13px">
              {{ $t('elite_credit_card_info') }}
            </p>

            <AddCreditCard ref="addCreditCardRef" v-model:cardholder="cardholder" @update:verify-error-status="updateIsCardInfoCorrect" />

            <DialogError v-model:show-status="isErrorSnackBarShown" class="errorSnackBar" :error="snackBarErrorMessage" />

            <!-- 驗證卡號是否符合 ELITE 會員資格 -->
            <v-btn
              class="submitButton text-white"
              :class="{ disable: !meta.valid || !isCardInfoCorrect || isButtonLoading }"
              type="submit"
              :min-width="66"
              :min-height="40"
              :color="!meta.valid || !isCardInfoCorrect || isButtonLoading ? 'orange30' : 'orange50'"
              variant="flat"
            >
              <Loading v-if="isButtonLoading" />
              <span v-else>{{ $t('elite_verify_membership') }}</span>
            </v-btn>
          </Form>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="successSection wd-bg-white wd-mb-6">
        <p class="text-h6 mb-6">{{ $t('elite_redeem_sucess') }}</p>
        <v-img
          class="logo"
          :style="isMobile ? 'width: 120px;' : 'width: 146px;'"
          cover
          src="@/assets/images/funnow/redemption_elite-success.svg?url"
        />
        <p class="periodTitle wd-mt-6 mb-0">{{ $t('membership_period') }}</p>
        <p class="periodValue mb-6 wd-text-orange50">{{ startTime }}-{{ endTime }}</p>

        <div class="appDownloadWrapper wd-bg-orange10">
          <AppDownloadSheet :is-elite-page="true" wrapper-class="wd-justify-center" />
        </div>
      </div>

      <div v-if="productList.length > 0" class="swiperWrapper wd-bg-white">
        <!-- ELITE product list -->
        <IndexProductSwiper
          :is-mobile="isMobile"
          header-class="wd-items-start"
          :can-showing-all-button-show-on-mobile="true"
          :title="$t('elite_free_signature')"
          title-class="medium__header__title"
          :is-branch-card-mode="false"
          :items="productList"
          :region-id="regionId"
          :category-id="categoryId"
          :category-type="categoryType"
          :source="GtmSource.ELITE_REDEMPTION"
        />
      </div>
      <div v-else class="wd--mt-6" />
    </template>
  </v-container>
</template>

<script setup lang="ts">
import * as memberService from '@/service/member'
import { EliteErrorCodeMessageMap, EliteRedemptionStep } from '@/view-models/member'
import { RegionIdMap } from '@/view-models/region'
import { GtmSource } from '@/view-models/gtm'
import { useSetupTapPay } from '@/composition/useSetupTapPay'
import { useUserInfo } from '@/composition/useMember'

import AddCreditCard from '@/components/page/preview/add-credit-card.vue'
import DialogError from '@/components/notice/error/dialog-error.vue'
import Loading from '@/components/icons/loading.vue'
import AppDownloadSheet from '@/components/AppDownloadSheet.vue'
import IndexProductSwiper from '@/components/swiper/product-swiper__index.vue'

const nuxtApp = useNuxtApp()
const route = useRoute()
const { $axios, $dayjs, $store: store, $cookiz, $i18n } = nuxtApp
const validationObserverRef = ref<any | null>(null)
const addCreditCardRef = useTemplateRef('addCreditCardRef')
useSetupTapPay()
const { userInfo } = useUserInfo()

const isMobile = ref(false)
const step = ref(EliteRedemptionStep.Redemption)
const cardholder = ref({
  name: '',
  phone: '',
  email: '',
})
const isCardInfoCorrect = ref(false)
const isButtonLoading = ref(false)
const isErrorSnackBarShown = ref(Boolean($cookiz.get('eliteErrorCode')) || false)
const errorCodeFromApi = ref('')
const errorCodeFromCookie = ref($cookiz.get('eliteErrorCode') || '')
const startTime = ref('')
const endTime = ref('')
const categoryId = ref('')
const categoryType = ref(0)
const productList = ref([])

const snackBarErrorMessage = computed(() => {
  const errorCode = errorCodeFromApi.value || errorCodeFromCookie.value
  const i18nKey = EliteErrorCodeMessageMap[errorCode] || EliteErrorCodeMessageMap.default

  return $i18n.t(i18nKey)
})
const isRedemptionStep = computed(() => step.value === EliteRedemptionStep.Redemption)
const regionId = computed(() => +(route.query.region_id || RegionIdMap.KualaLumpur))

// add onMounted for user who viewed else page first, then go here
onMounted(() => {
  onResize()
  $cookiz.remove('eliteErrorCode')

  checkLogin()
})

// `watch` is for user who views this page first
watch(userInfo, checkLogin)

function onResize() {
  isMobile.value = window.innerWidth < 600
}

async function checkLogin() {
  if (!userInfo.value) {
    openLoginDialog()
    return
  }

  if (!userInfo.value.is_elite) {
    step.value = EliteRedemptionStep.Redemption
    return
  }

  await getEliteAndSetStep()
}

async function getEliteAndSetStep() {
  const res = await memberService.getMemberElite($axios as any, { region_id: regionId.value })

  if (res.is_credit) {
    step.value = EliteRedemptionStep.Success
    await setEliteInfo(res)
  } else {
    step.value = EliteRedemptionStep.Redemption
  }
}

function openLoginDialog() {
  store.dispatch('dialog/setLoginDialog', { state: true, options: { modalIntroBrand: 'ELITE', canClose: false } })
}

function updateIsCardInfoCorrect(status: any) {
  isCardInfoCorrect.value = !status
}

async function handleVerify() {
  isErrorSnackBarShown.value = false
  isButtonLoading.value = true
  const { valid } = await validationObserverRef.value!.validate()
  if (!valid || !isCardInfoCorrect.value) {
    isButtonLoading.value = false
    return
  }
  getPayloadAndRequestElite()
}

async function getPayloadAndRequestElite() {
  const cardInfoPayload: any = {}
  const primePayload = await addCreditCardRef.value!.getPrime()
  if (!primePayload) {
    isButtonLoading.value = false
    isErrorSnackBarShown.value = true
    errorCodeFromApi.value = 'default'
    return
  }

  cardInfoPayload.prime = primePayload.card.prime
  requestElite(cardInfoPayload.prime)
}

async function requestElite(prime: string = '') {
  const payload = {
    prime,
    cardholder: cardholder.value,
    region_id: regionId.value,
  }
  try {
    const res = await memberService.requestElite($axios as any, payload)
    if (res.code !== 0) {
      throw res
    }

    $cookiz.set('eliteRedirectTo', nuxtApp.$localePath(`/elite-redemption?region_id=${regionId.value}`), { path: '/', maxAge: 600 })
    window.location = res.data.payment_url
  } catch (error: any) {
    console.error('requestElite erros, error is: ', error.message)
    isButtonLoading.value = false
    isErrorSnackBarShown.value = true
    errorCodeFromApi.value = error.code
  }
}

async function setEliteInfo(info: any) {
  startTime.value = $dayjs(info.start_time).format('YYYY/MM/DD')
  endTime.value = $dayjs(info.end_time).format('YYYY/MM/DD')

  if (!info.category?.id) {
    return
  }
  categoryId.value = info.category.id
  categoryType.value = info.category.type

  const params = {
    filter_regionid: regionId.value,
    categoryId: info.category.id,
    start: 0,
    size: 8,
    sortby: 'popular',
  }

  const data = await store
    .dispatch('product/getProductList', params)
    .then((res: any) => {
      if (res.code === 0) {
        return res.data
      }
      throw res
    })
    .catch((error: any) => {
      console.error('getEliteProductList errors, error is: ', error)
    })

  productList.value = data || []
}
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  margin: 40px auto 28px;
  padding: 0;

  .eliteIntroSection {
    padding: 16px 0 20px;
    border-radius: 4px;

    &__description {
      margin: 0 160px;
    }
  }

  h2 {
    font-size: 20px;
    line-height: 28px;
    font-weight: 500;
    margin: 12px 20px;
  }

  .creditCardDetailSection {
    border-radius: 4px;
    padding: 16px 0 40px;

    &__content {
      margin: 0 160px;

      .cardHolderTitle {
        padding: 12px 0 8px;
      }
      .cardHolderTextField {
        margin-bottom: 6px;
      }

      .errorSnackBar {
        margin-top: 40px;
        margin-bottom: -28px;
      }

      .submitButton {
        font-size: 17px;
        width: 100%;
        margin-top: 40px;

        &.disable {
          pointer-events: none;
        }
      }
    }
  }

  .successSection {
    width: 100%;
    text-align: center;
    border-radius: 4px;
    overflow: hidden;

    .title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 500;
      margin: 40px 0 24px;
    }
    .logo {
      margin: 0 auto;
    }

    .periodTitle {
      font-size: 17px;
      line-height: 24px;
    }
    .periodValue {
      font-weight: 500;
      font-size: 20px;
      line-height: 28px;
    }
    .appDownloadWrapper {
      text-align: left;
      padding: 40px 0 20px;

      .guide-download-content {
        color: $orange50;
      }
    }
  }

  .swiperWrapper {
    width: 100%;
    padding: 16px 20px 0;
    border-radius: 4px;

    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @include xs {
    margin: 0;
    padding: 24px 0px 12px;
    width: auto;

    .eliteIntroSection {
      padding: 0 20px;

      &__description {
        margin: 0;
      }
    }

    h2 {
      font-size: 17px;
      line-height: 24px;
      margin: 12px 0;
    }

    .creditCardDetailSection {
      padding: 0 20px 32px;

      &__content {
        margin: 0;

        .cardHolderTitle {
          padding: 24px 0 8px;
        }

        .errorSnackBar {
          margin-top: 20px;
          margin-bottom: 0;
        }

        .submitButton {
          margin-top: 32px;
        }
      }
    }

    .successSection {
      .title {
        margin: 16px 20px 24px;
        font-size: 17px;
        line-height: 24px;
      }

      .periodTitle {
        font-size: 15px;
        line-height: 22px;
      }
      .periodValue {
        font-size: 17px;
        line-height: 24px;
      }
    }
  }

  @include xxs {
    .eliteIntroSection {
      border-radius: unset;
    }
    .creditCardDetailSection {
      border-radius: unset;
    }
    .successSection {
      border-radius: unset;

      .appDownloadWrapper {
        text-align: center;
        padding: 24px 20px 0;
      }
    }
    .swiperWrapper {
      padding: 0 0 0 20px;
      border-radius: unset;
    }
  }
}
</style>
